<template>
  <div class="mt-1">
    <template v-if="embedded">
      <b-overlay :show="loading" rounded="sm" no-fade>
        <div style="max-height: 400px; overflow-y: auto; overflow-x: auto;">
          <b-table
            :items="items"
            :fields="embeddedFields"
            small
            bordered
            show-empty
            empty-text="No applications"
            sticky-header
            class="table-responsive"
          >
            <template #cell(user)="{ item }">{{
              (item.user && item.user.name + " " + (item.user.surname || "")) ||
                "-"
            }}</template>
            <template #cell(status)="{ item }">
              <b-badge :variant="statusVariant(item.status)">{{
                statusText(item.status)
              }}</b-badge>
            </template>
            <template #cell(apply_reason)="{ item }">
              <div
                style="max-width: 200px; word-wrap: break-word; font-size: 0.85em;"
              >
                {{ item.apply_reason || "-" }}
              </div>
            </template>
            <template #cell(special_needs)="{ item }">
              <div
                style="max-width: 200px; word-wrap: break-word; font-size: 0.75em;"
              >
                {{ item.special_needs || '-' }}
              </div>
            </template>
            <template #cell(manager)="{ item }">
              <div style="min-width: 150px;">
                <!-- Assigned Manager -->
                <div v-if="item.assigned_manager" class="mb-1">
                  <small class="text-muted">Assigned:</small><br />
                  <b-badge variant="primary" class="mr-1 mb-1">{{
                    item.assigned_manager.name +
                      " " +
                      (item.assigned_manager.surname || "")
                  }}</b-badge>
                </div>
                <!-- User's Managers -->
                <div
                  v-if="item.event_managers && item.event_managers.length > 0"
                >
                  <small class="text-info font-weight-600"
                    >User's Managers:</small
                  ><br />
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="manager in item.event_managers"
                      :key="manager.id"
                      variant="info"
                      class="mr-1 mb-1"
                    >
                      {{ manager.name + " " + (manager.surname || "") }}
                    </b-badge>
                  </div>
                </div>
                <!-- No managers -->
                <span
                  v-if="
                    !item.assigned_manager &&
                      (!item.event_managers || item.event_managers.length === 0)
                  "
                  >-</span
                >
              </div>
            </template>
            <template #cell(admin)="{ item }">
              <div style="min-width: 150px;">
                <!-- Assigned Admin -->
                <div v-if="item.assigned_admin" class="mb-1">
                  <small class="text-muted">Assigned:</small><br />
                  <b-badge variant="success" class="mr-1 mb-1">{{
                    item.assigned_admin.name +
                      " " +
                      (item.assigned_admin.surname || "")
                  }}</b-badge>
                </div>
                <!-- Event's Admins -->
                <div v-if="item.event_admins && item.event_admins.length > 0">
                  <small class="text-success font-weight-600"
                    >Event's Admins:</small
                  ><br />
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="admin in item.event_admins"
                      :key="admin.id"
                      variant="success"
                      class="mr-1 mb-1"
                    >
                      {{ admin.name + " " + (admin.surname || "") }}
                    </b-badge>
                  </div>
                </div>
                <!-- No admins -->
                <span
                  v-if="
                    !item.assigned_admin &&
                      (!item.event_admins || item.event_admins.length === 0)
                  "
                  >-</span
                >
              </div>
            </template>
            <template #cell(created_at)="{ item }">{{
              item.created_at ? dateFormat(item.created_at) : "-"
            }}</template>
            <template #cell(actions)="{ item }">
              <div style="min-width: 200px;" class="text-center">
                <!-- Manager Approval Buttons -->
                <div
                  v-if="canManagerApprove(item) && isUserDepartmentManager()"
                  class="mb-25"
                >
                  <b-button
                    variant="success"
                    size="sm"
                    class="mr-25"
                    @click="managerApprove(item)"
                  >
                    <i class="fa-solid fa-check mr-25"></i>Approve
                  </b-button>
                  <b-button
                    variant="danger"
                    size="sm"
                    @click="managerReject(item)"
                  >
                    <i class="fa-solid fa-times mr-25"></i>Reject
                  </b-button>
                </div>

                <!-- Admin Approval Buttons -->
                <div v-if="canAdminApprove(item)" class="mb-25">
                  <b-button
                    variant="success"
                    size="sm"
                    class="mr-25"
                    @click="accept(item)"
                  >
                    <i class="fa-solid fa-check mr-25"></i>Accept
                  </b-button>
                  <b-button
                    v-if="canAdminReject(item)"
                    variant="danger"
                    size="sm"
                    class="mr-25"
                    @click="reject(item)"
                  >
                    <i class="fa-solid fa-times mr-25"></i>Reject
                  </b-button>
                  <b-button
                    v-if="canAdminWaitlist(item)"
                    variant="warning"
                    size="sm"
                    @click="waitlist(item)"
                  >
                    <i class="fa-solid fa-clock mr-25"></i>Waitlist
                  </b-button>
                  <!-- Send Reminder Button (only for pending_manager_approval) -->
                  <b-button
                    v-if="item.status === 'pending_manager_approval'"
                    variant="info"
                    size="sm"
                    class="ml-25"
                    @click="sendReminderToManager(item)"
                  >
                    <i class="fa-solid fa-bell mr-25"></i>Send Reminder
                  </b-button>
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </b-overlay>
    </template>
    <template v-else>
      <b-card>
        <b-overlay :show="loading" rounded="sm" no-fade>
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h3 class="mb-0">Applications</h3>
            <div>
              <b-button
                size="sm"
                variant="success"
                class="mr-2"
                @click="exportToExcel"
                :disabled="!items.length"
              >
                <i class="fa fa-file-excel mr-1"></i>
                Export Excel
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                :to="{ name: 'dashboard' }"
                >Back to Events</b-button
              >
            </div>
          </div>

          <b-table
            :items="items"
            :fields="fields"
            small
            bordered
            responsive="sm"
            show-empty
            empty-text="No applications"
          >
            <template #cell(user)="{ item }">{{
              (item.user && item.user.name + " " + (item.user.surname || "")) ||
                "-"
            }}</template>
            <template #cell(status)="{ item }">
              <b-badge :variant="statusVariant(item.status)">{{
                statusText(item.status)
              }}</b-badge>
            </template>
            <template #cell(apply_reason)="{ item }">
              <div
                style="max-width: 200px; word-wrap: break-word; font-size: 0.85em;"
              >
                {{ item.apply_reason || "-" }}
              </div>
            </template>
            <template #cell(special_needs)="{ item }">
              <div
                style="max-width: 200px; word-wrap: break-word; font-size: 0.75em;"
              >
                {{ item.special_needs || '-' }}
              </div>
            </template>
            <template #cell(manager)="{ item }">
              <div style="min-width: 150px;">
                <!-- Assigned Manager -->
                <div v-if="item.assigned_manager" class="mb-1">
                  <small class="text-muted">Assigned:</small><br />
                  <b-badge variant="primary" class="mr-1 mb-1">{{
                    item.assigned_manager.name +
                      " " +
                      (item.assigned_manager.surname || "")
                  }}</b-badge>
                </div>
                <!-- User's Managers -->
                <div
                  v-if="item.event_managers && item.event_managers.length > 0"
                >
                  <small class="text-info font-weight-600"
                    >User's Managers:</small
                  ><br />
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="manager in item.event_managers"
                      :key="manager.id"
                      variant="info"
                      class="mr-1 mb-1"
                    >
                      {{ manager.name + " " + (manager.surname || "") }}
                    </b-badge>
                  </div>
                </div>
                <!-- No managers -->
                <span
                  v-if="
                    !item.assigned_manager &&
                      (!item.event_managers || item.event_managers.length === 0)
                  "
                  >-</span
                >
              </div>
            </template>
            <template #cell(admin)="{ item }">
              <div style="min-width: 150px;">
                <!-- Assigned Admin -->
                <div v-if="item.assigned_admin" class="mb-1">
                  <small class="text-muted">Assigned:</small><br />
                  <b-badge variant="success" class="mr-1 mb-1">{{
                    item.assigned_admin.name +
                      " " +
                      (item.assigned_admin.surname || "")
                  }}</b-badge>
                </div>
                <!-- Event's Admins -->
                <div v-if="item.event_admins && item.event_admins.length > 0">
                  <small class="text-success font-weight-600"
                    >Event's Admins:</small
                  ><br />
                  <div class="d-flex flex-wrap">
                    <b-badge
                      v-for="admin in item.event_admins"
                      :key="admin.id"
                      variant="success"
                      class="mr-1 mb-1"
                    >
                      {{ admin.name + " " + (admin.surname || "") }}
                    </b-badge>
                  </div>
                </div>
                <!-- No admins -->
                <span
                  v-if="
                    !item.assigned_admin &&
                      (!item.event_admins || item.event_admins.length === 0)
                  "
                  >-</span
                >
              </div>
            </template>
            <template #cell(created_at)="{ item }">{{
              item.created_at ? dateFormat(item.created_at) : "-"
            }}</template>
            <template #cell(actions)="{ item }">
              <div style="min-width: 200px;" class="text-center">
                <!-- Manager Approval Buttons -->
                <div
                  v-if="canManagerApprove(item) && isUserDepartmentManager()"
                  class="mb-25"
                >
                  <b-button
                    variant="success"
                    size="sm"
                    class="mr-25"
                    @click="managerApprove(item)"
                  >
                    <i class="fa-solid fa-check mr-25"></i>Approve
                  </b-button>
                  <b-button
                    variant="danger"
                    size="sm"
                    @click="managerReject(item)"
                  >
                    <i class="fa-solid fa-times mr-25"></i>Reject
                  </b-button>
                </div>

                <!-- Admin Approval Buttons -->
                <div v-if="canAdminApprove(item)" class="mb-25">
                  <b-button
                    variant="success"
                    size="sm"
                    class="mr-25"
                    @click="accept(item)"
                  >
                    <i class="fa-solid fa-check mr-25"></i>Accept
                  </b-button>
                  <b-button
                    v-if="canAdminReject(item)"
                    variant="danger"
                    size="sm"
                    class="mr-25"
                    @click="reject(item)"
                  >
                    <i class="fa-solid fa-times mr-25"></i>Reject
                  </b-button>
                  <b-button
                    v-if="canAdminWaitlist(item)"
                    variant="warning"
                    size="sm"
                    @click="waitlist(item)"
                  >
                    <i class="fa-solid fa-clock mr-25"></i>Waitlist
                  </b-button>
                  <!-- Send Reminder Button (only for pending_manager_approval) -->
                  <b-button
                    v-if="item.status === 'pending_manager_approval'"
                    variant="info"
                    size="sm"
                    class="ml-25"
                    @click="sendReminderToManager(item)"
                  >
                    <i class="fa-solid fa-bell mr-25"></i>Send Reminder
                  </b-button>
                </div>
              </div>
            </template>
          </b-table>
        </b-overlay>
      </b-card>
    </template>
  </div>
</template>
<script>
import { BOverlay, BCard, BTable, BButton, BBadge } from "bootstrap-vue";
import store from "@/store";
import eventsStoreModule from "./eventsStoreModule";
import { onUnmounted } from "@vue/composition-api";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import * as XLSX from "xlsx";

export default {
  props: {
    eventId: { type: [String, Number], required: false },
    embedded: { type: Boolean, default: false },
  },
  components: {
    BOverlay,
    BCard,
    BTable,
    BButton,
    BBadge,
    ToastificationContent,
  },
  data() {
    return {
      loading: false,
      items: [],
      event: null,
      fields: [
        { key: "id", label: "ID" },
        { key: "user", label: "User" },
        { key: "status", label: "Status" },
        { key: "apply_reason", label: "Apply Reason" },
        { key: "special_needs", label: "Special Needs" },
        { key: "manager", label: "Manager" },
        { key: "admin", label: "Admin" },
        { key: "created_at", label: "Created" },
        { key: "actions", label: "Actions" },
      ],
      embeddedFields: [
        { key: "user", label: "User" },
        { key: "status", label: "Status" },
        { key: "apply_reason", label: "Apply Reason" },
        { key: "special_needs", label: "Special Needs" },
        { key: "manager", label: "Manager" },
        { key: "admin", label: "Admin" },
        { key: "created_at", label: "Created" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  setup() {
    const MODULE = "events";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, eventsStoreModule);
    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
    });
  },
  created() {
    this.load();
  },
  methods: {
    statusVariant(s) {
      switch (s) {
        case "accepted":
          return "light-success";
        case "rejected":
        case "manager_rejected":
          return "light-danger";
        case "pending_manager_approval":
          return "light-warning";
        case "pending_admin_review":
          return "light-info";
        case "waitlisted":
        case "waitlist":
          return "light-warning";
        case "withdrawn":
        case "cancelled":
          return "light-secondary";
        case "draft":
        case "submitted":
          return "light-primary";
        default:
          return "light-secondary";
      }
    },
    statusText(s) {
      switch (s) {
        case "draft":
          return "Draft";
        case "submitted":
          return "Submitted";
        case "pending_manager_approval":
          return "Pending Manager Approval";
        case "manager_rejected":
          return "Manager Rejected";
        case "pending_admin_review":
          return "Under Admin Review";
        case "waitlisted":
        case "waitlist":
          return "Waitlisted";
        case "accepted":
          return "Accepted";
        case "rejected":
          return "Rejected";
        case "withdrawn":
          return "Withdrawn";
        case "cancelled":
          return "Cancelled";
        default:
          return s;
      }
    },
    dateFormat(val) {
      if (!val) return "-";
      const date = new Date(val);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${day}-${month}-${year} ${time}`;
    },
    async load() {
      try {
        this.loading = true;
        const id =
          this.eventId ||
          (this.$route && this.$route.params && this.$route.params.id);

        // Load applications
        const res = await store.dispatch("events/listApplications", { id });
        console.log("EventApplications loaded data:", res.data);
        this.items = res.data;

        // Load event details for export
        if (!this.embedded) {
          const eventRes = await store.dispatch("events/getEventById", { id });
          this.event = eventRes.data;
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Failed to load applications",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async accept(item) {
      try {
        // if (!this.$Can || !this.$Can("manage_event_applications")) return;
        await store.dispatch("events/adminAccept", { applicationId: item.id });
        item.status = "accepted";
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Accepted",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Action failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    async reject(item) {
      try {
        // if (!this.$Can || !this.$Can("manage_event_applications")) return;

        // Onay modalı göster
        const result = await this.$swal({
          title: "Are you sure?",
          text: "Do you want to reject this application?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, reject it!",
          cancelButtonText: "Cancel",
          customClass: {
            confirmButton: "btn btn-danger",
            cancelButton: "btn btn-secondary ml-1",
          },
          buttonsStyling: false,
        });

        if (result.isConfirmed) {
          await store.dispatch("events/adminReject", {
            applicationId: item.id,
          });
          item.status = "rejected";
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Rejected",
              icon: "AlertCircleIcon",
              variant: "warning",
            },
          });
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Action failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    async waitlist(item) {
      try {
        // if (!this.$Can || !this.$Can("manage_event_applications")) return;
        await store.dispatch("events/adminWaitlist", {
          applicationId: item.id,
        });
        item.status = "waitlisted";
        this.$toast({
          component: ToastificationContent,
          props: { title: "Waitlisted", icon: "InfoIcon", variant: "info" },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Action failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    isManagerOrAdmin() {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userRole = userData.role || "";
      const userId = userData.id;

      // Sistem admin'i ise true (Department Manager kaldırıldı)
      if (["admin", "supermanager", "Administrator"].includes(userRole)) {
        return true;
      }

      // Event admin kontrolü - event'in admin'lerinden biri mi?
      if (this.event && this.event.admins && userId) {
        return this.event.admins.some((admin) => admin.id === userId);
      }

      return false;
    },
    exportToExcel() {
      if (!this.items.length || !this.event) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "No data to export",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Prepare data for export
      const exportData = this.items.map((item, index) => ({
        ID: item.id,
        User: item.user
          ? `${item.user.name} ${item.user.surname || ""}`.trim()
          : "-",
        Email: item.user ? item.user.email || "-" : "-",
        Company: item.user && item.user.company ? item.user.company.title : "-",
        Status: this.statusText(item.status),
        "Apply Reason": item.apply_reason || "-",
        "Special Needs": item.special_needs || "-",
        Manager: item.assigned_manager
          ? `${item.assigned_manager.name} ${item.assigned_manager.surname ||
              ""}`.trim()
          : "-",
        Admin: item.assigned_admin
          ? `${item.assigned_admin.name} ${item.assigned_admin.surname ||
              ""}`.trim()
          : "-",
        "Applied Date": item.created_at
          ? this.dateFormat(item.created_at)
          : "-",
        "Accepted Date": item.accepted_at
          ? this.dateFormat(item.accepted_at)
          : "-",
        "Rejected Date": item.rejected_at
          ? this.dateFormat(item.rejected_at)
          : "-",
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Generate filename: event name - event title
      const eventName = this.event.course ? this.event.course.title : "Event";
      const eventTitle = this.event.title_override || eventName;
      const filename = `${eventName} - ${eventTitle}.xlsx`;

      XLSX.utils.book_append_sheet(wb, ws, "Applications");

      // Export file
      XLSX.writeFile(wb, filename);

      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Excel Exported",
          text: `File exported: ${filename}`,
          icon: "CheckIcon",
          variant: "success",
        },
      });
    },

    // Send Reminder Method
    async sendReminderToManager(item) {
      try {
        this.loading = true;

        // Check if there are any managers (assigned or user's managers)
        const hasAssignedManager =
          item.assigned_manager && item.assigned_manager.id;
        const hasUserManagers =
          item.event_managers && item.event_managers.length > 0;

        if (!hasAssignedManager && !hasUserManagers) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "No manager assigned to this application.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // If there's an assigned manager, send to assigned manager
        // Otherwise, send to all user's managers
        if (hasAssignedManager) {
          await store.dispatch("events/sendReminderToManager", {
            applicationId: item.id,
            managerId: item.assigned_manager.id,
            message: `Please review the pending application for ${
              item.user ? item.user.name : "this user"
            }.`,
          });
        } else {
          // Send reminder to all user's managers
          for (const manager of item.event_managers) {
            await store.dispatch("events/sendReminderToManager", {
              applicationId: item.id,
              managerId: manager.id,
              message: `Please review the pending application for ${
                item.user ? item.user.name : "this user"
              }.`,
            });
          }
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Reminder Sent",
            text: "Reminder sent to manager(s) successfully.",
            icon: "BellIcon",
            variant: "success",
          },
        });

        // Refresh applications
        await this.loadApplications();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to send reminder.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    // User role checking methods
    isManagerOrAdmin() {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userRole = userData.role || "";
      // Department Manager'ı kaldırdık - sadece gerçek admin roller
      return ["admin", "supermanager", "Administrator"].includes(userRole);
    },

    isUserDepartmentManager() {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userRole = userData.role || "";
      return userRole === "Department Manager";
    },

    canUserManageApplication(item) {
      // Prefer application-provided event_admins; fallback to loaded event.admins
      const contextAdmins = (item && item.event_admins) || (this.event && this.event.admins) || [];
      return this.$CanEvent('view_event_applications', contextAdmins);
    },

    // Manager approval methods
    canManagerApprove(item) {
      if (!this.isUserDepartmentManager()) return false;

      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const currentUserId = userData.id;

      // Current user'ın bu user'ın manager'ları arasında olup olmadığını kontrol et
      const isUserManager =
        item.event_managers &&
        item.event_managers.some((manager) => manager.id === currentUserId);

      const canApprove =
        item.status === "pending_manager_approval" &&
        !item.assigned_manager &&
        isUserManager;

      return canApprove;
    },

    async managerApprove(item) {
      try {
        this.loading = true;
        await store.dispatch("events/managerApproveApplication", {
          applicationId: item.id,
        });

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Application Approved",
            text: "Application has been approved and sent for admin review.",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Refresh applications
        await this.load();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: e.response?.data?.message || "Failed to approve application",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    async managerReject(item) {
      try {
        this.loading = true;
        await store.dispatch("events/managerRejectApplication", {
          applicationId: item.id,
        });

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Application Rejected",
            text: "Application has been rejected by manager.",
            icon: "XIcon",
            variant: "warning",
          },
        });

        // Refresh applications
        await this.load();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: e.response?.data?.message || "Failed to reject application",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    // Admin approval methods
    canAdminApprove(item) {
      if (!this.canUserManageApplication(item)) return false;

      // Admin: pending_admin_review, pending_manager_approval veya waitlisted durumunda onaylayabilir
      // Admin'ler manager yerine de onay yapabilir, waitlisted'den de accept edebilir
      // Waitlisted durumunda assigned_admin kontrolü yapılmaz çünkü zaten admin atanmış olabilir
      if (item.status === "waitlisted") {
        return true;
      }

      const canApprove =
        (item.status === "pending_admin_review" ||
          item.status === "pending_manager_approval" ||
          item.status === "manager_rejected") &&
        !item.assigned_admin;
      return canApprove;
    },

    canAdminWaitlist(item) {
      if (!this.canUserManageApplication(item)) return false;

      return (
        (item.status === "pending_admin_review" ||
          item.status === "pending_manager_approval" ||
          item.status === "manager_rejected" ||
          item.status === "accepted") &&
        item.status !== "waitlisted"
      );
    },

    canAdminReject(item) {
      if (!this.canUserManageApplication(item)) return false;

      return !["rejected", "withdrawn", "cancelled"].includes(item.status);
    },

    canAdminApprove(item) {
      // Admin: pending_admin_review, pending_manager_approval veya waitlisted durumunda onaylayabilir
      if (!this.canUserManageApplication(item)) {
        return false;
      }

      // Waitlisted durumunda assigned_admin kontrolü yapılmaz çünkü zaten admin atanmış olabilir
      if (item.status === "waitlisted") {
        return true;
      }

      const canApprove =
        (item.status === "pending_admin_review" ||
          item.status === "pending_manager_approval") &&
        !item.assigned_admin;
      return canApprove;
    },

    canAdminWaitlist(item) {
      // Admin: pending_admin_review, pending_manager_approval veya accepted durumunda waitlist'e alabilir
      if (!this.canUserManageApplication(item)) {
        return false;
      }

      return [
        "pending_admin_review",
        "pending_manager_approval",
        "accepted",
      ].includes(item.status);
    },

    canManagerApprove(item) {
      // Manager: pending_manager_approval durumunda ve assigned_manager_id null ise onaylayabilir
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const currentUserId = userData.id;

      // Current user'ın bu user'ın manager'ları arasında olup olmadığını kontrol et
      const isUserManager =
        item.event_managers &&
        item.event_managers.some((manager) => manager.id === currentUserId);

      const canApprove =
        item.status === "pending_manager_approval" &&
        !item.assigned_manager &&
        isUserManager;

      return canApprove;
    },

    isUserDepartmentManager() {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      return userData.role === "Department Manager";
    },
  },
};
</script>
<style scoped>
/* Actions column için horizontal scroll optimizasyonu */
.table-responsive {
  white-space: nowrap;
}

.table-responsive td:last-child {
  min-width: 300px;
  white-space: nowrap;
}

/* Button spacing */
.table-responsive .btn {
  margin-right: 4px;
  margin-bottom: 2px;
}
</style>
